import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getHttpErrorMessage as getErrorMessage } from '@/services/httpRetry.ts';
import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconDownload,
  IconFile,
  IconX,
} from '@tabler/icons-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CustomDropZone from '@/components/CustomDropZone.tsx';
import {
  getSignedUploadConfig,
  normalizeUploadContentType,
  relativeFileToUploadMetadata,
} from '@/hooks/api/datasets/uploadMetadata.ts';
import { ManualTriggerInputType } from '@/types/automationSteps.ts';
import { RelativeFile } from '@/types/common.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import { formatBytes } from '@/utils/misc.ts';
import {
  createPublicPortalRun,
  processPublicPortalRunInputs,
  retrievePublicPortal,
  retrievePublicPortalRun,
} from '@/pages/PublicPortal/api.ts';
import {
  PublicPortal,
  PublicPortalJsonFormField,
  PublicPortalManualTriggerInput,
  PublicPortalRun,
  PublicPortalRunCreateResponse,
  PublicPortalRunTriggerResponse,
  PublicPortalTriggerDirectoryRequest,
  PublicPortalTriggerFileRequest,
  PublicPortalTriggerJsonRequest,
} from '@/pages/PublicPortal/types.ts';
import classes from '@/pages/PublicPortal/PublicPortalPage.module.css';

type TriggerValue =
  | { trigger_id: string; type: ManualTriggerInputType.File; files: RelativeFile[] }
  | { trigger_id: string; type: ManualTriggerInputType.Folder; files: RelativeFile[]; name: string }
  | { trigger_id: string; type: ManualTriggerInputType.Text; text: string }
  | { trigger_id: string; type: ManualTriggerInputType.Json; data: Record<string, unknown> };

type PortalLocationState = {
  portalRunError?: string;
} | null;

type SubmitPhase = 'create' | 'upload' | 'process';

const OUTPUT_READY_STATUSES = new Set<ExecutionStatus>([
  ExecutionStatus.Completed,
  ExecutionStatus.PartiallyCompleted,
]);
const SUBMIT_ANOTHER_STATUSES = new Set<ExecutionStatus>([
  ExecutionStatus.Completed,
  ExecutionStatus.PartiallyCompleted,
  ExecutionStatus.Errors,
  ExecutionStatus.Cancelled,
  ExecutionStatus.AskUser,
]);
const FAILURE_STATUSES = new Set<ExecutionStatus>([
  ExecutionStatus.Errors,
  ExecutionStatus.Cancelled,
]);
const PORTAL_RUN_ISSUE_LABELS: Partial<
  Record<ExecutionStatus, { badge: string; heading: string }>
> = {
  [ExecutionStatus.Errors]: {
    badge: 'Failed',
    heading: 'Run failed',
  },
  [ExecutionStatus.Cancelled]: {
    badge: 'Cancelled',
    heading: 'Run cancelled',
  },
  [ExecutionStatus.AskUser]: {
    badge: 'Needs attention',
    heading: 'Run needs attention',
  },
};
const MAX_PORTAL_TOTAL_UPLOAD_FILES = 100;
const MAX_PORTAL_DIRECTORY_FILES = 100;
const MAX_PORTAL_UPLOAD_PATH_LENGTH = 512;
const MAX_PORTAL_TEXT_INPUT_CHARS = 100_000;

function PublicPortalPage() {
  const { portalIdentifier, publicRunToken: routePublicRunToken } = useParams<{
    portalIdentifier: string;
    publicRunToken?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [portal, setPortal] = useState<PublicPortal | null>(null);
  const [portalRun, setPortalRun] = useState<PublicPortalRun | null>(null);
  const [submittedPublicRunToken, setSubmittedPublicRunToken] = useState<string | null>(null);
  const [runName, setRunName] = useState('');
  const [values, setValues] = useState<Record<string, TriggerValue>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [staleForm, setStaleForm] = useState(false);
  const formGeneration = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const activePublicRunToken = routePublicRunToken ?? submittedPublicRunToken;
  const shouldPollCurrentPortalRun = !portalRun || shouldPollPortalRun(portalRun);
  const canonicalPortalIdentifier = portal?.public_slug || portalIdentifier;
  const portalDescription =
    portal?.description || 'Upload the requested inputs and start the automation.';

  useEffect(() => {
    if (!portalIdentifier) return;

    let cancelled = false;
    const generation = ++formGeneration.current;
    setLoading(true);
    setSubmitting(false);
    setError(null);
    setStaleForm(false);
    setPortal(null);
    setPortalRun(null);
    setSubmittedPublicRunToken(null);
    retrievePublicPortal(portalIdentifier)
      .then((nextPortal) => {
        if (cancelled) return;
        setPortal(nextPortal);
        setValues(makeInitialValues(nextPortal.manual_trigger_inputs));
        if (nextPortal.public_slug && nextPortal.public_slug !== portalIdentifier) {
          const nextPath = routePublicRunToken
            ? `/portals/${nextPortal.public_slug}/runs/${routePublicRunToken}`
            : `/portals/${nextPortal.public_slug}`;
          navigate(nextPath, { replace: true });
        }
      })
      .catch((nextError) => {
        if (!cancelled) setError(getErrorMessage(nextError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      formGeneration.current = generation + 1;
    };
  }, [navigate, portalIdentifier, routePublicRunToken]);

  useEffect(() => {
    if (!activePublicRunToken) return;
    setError(null);
    setPortalRun(null);
  }, [activePublicRunToken]);

  useEffect(() => {
    const portalRunError = (location.state as PortalLocationState)?.portalRunError;
    if (!portalRunError) return;

    setError(portalRunError);
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null },
    );
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!canonicalPortalIdentifier || !activePublicRunToken || !shouldPollCurrentPortalRun) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const nextRun = await retrievePublicPortalRun(
          canonicalPortalIdentifier,
          activePublicRunToken,
        );
        if (!cancelled) setPortalRun(nextRun);
      } catch (nextError) {
        if (!cancelled) setError(getErrorMessage(nextError));
      }
    };

    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activePublicRunToken, canonicalPortalIdentifier, shouldPollCurrentPortalRun]);

  const submitDisabled = useMemo(() => {
    if (!portal || submitting || activePublicRunToken || staleForm) return true;
    return portal.manual_trigger_inputs.some(
      (input) => input.is_required && !hasValue(values[input.output_id]),
    );
  }, [activePublicRunToken, portal, submitting, values, staleForm]);

  const updateValue = useCallback((triggerId: string, value: TriggerValue) => {
    setValues((current) => ({ ...current, [triggerId]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!portal || !canonicalPortalIdentifier || staleForm) return;
    setError(null);
    const normalizedRunName = runName.trim();
    let createdPublicRunToken: string | null = null;
    let submitPhase: SubmitPhase = 'create';

    try {
      const inputError = validatePortalInputs(portal.manual_trigger_inputs, values);
      if (inputError) {
        setError(inputError);
        return;
      }

      setSubmitting(true);
      const createResponse = await createPublicPortalRun(canonicalPortalIdentifier, {
        selection_token: portal.selection_token,
        run_name: normalizedRunName || null,
        triggers: makeTriggerRequests(portal.manual_trigger_inputs, values),
      });
      createdPublicRunToken = createResponse.public_run_token;

      setSubmittedPublicRunToken(createResponse.public_run_token);
      navigate(`/portals/${canonicalPortalIdentifier}/runs/${createResponse.public_run_token}`, {
        replace: true,
      });
      submitPhase = 'upload';
      await uploadTriggerInputs(createResponse, values);
      submitPhase = 'process';
      const processedRun = await processPublicPortalRunInputs(
        canonicalPortalIdentifier,
        createResponse.public_run_token,
      );
      setPortalRun(processedRun);
    } catch (nextError) {
      if (
        !createdPublicRunToken &&
        axios.isAxiosError(nextError) &&
        nextError.response?.status === 409
      )
        setStaleForm(true);
      if (createdPublicRunToken && submitPhase === 'upload') {
        void processPublicPortalRunInputs(canonicalPortalIdentifier, createdPublicRunToken).catch(
          () => undefined,
        );
      }
      const errorMessage =
        createdPublicRunToken && submitPhase === 'upload'
          ? getUploadFailureMessage()
          : getErrorMessage(nextError);
      if (createdPublicRunToken) {
        navigate(`/portals/${canonicalPortalIdentifier}/runs/${createdPublicRunToken}`, {
          replace: true,
          state: { portalRunError: errorMessage },
        });
      } else {
        setError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  }, [canonicalPortalIdentifier, navigate, portal, runName, values, staleForm]);

  if (loading) {
    return (
      <div className={classes.page}>
        <div className={classes.shell}>
          <Group gap="sm">
            <Loader size="sm" />
            <Text>Loading portal...</Text>
          </Group>
        </div>
      </div>
    );
  }

  if (!portal) {
    return (
      <div className={classes.page}>
        <div className={classes.shell}>
          <Alert color="red" icon={<IconAlertCircle size={18} />}>
            {error || 'This portal is not available.'}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <main className={classes.shell}>
        <Stack gap={0}>
          <Group className={classes.header} gap="md" align="center" wrap="nowrap">
            {portal.logo_url && (
              <div className={classes.logoFrame}>
                <img className={classes.logo} src={portal.logo_url} alt="" />
              </div>
            )}
            <Stack gap={2} className={classes.headerText}>
              <Title order={1} className={classes.portalTitle}>
                {portal.title}
              </Title>
              <Text className={classes.portalDescription}>{portalDescription}</Text>
            </Stack>
          </Group>

          <section className={classes.panel}>
            <Stack className={classes.panelBody} gap="lg">
              {portalRun && (
                <Group justify="flex-end">
                  <RunStatusBadge status={portalRun.status} />
                </Group>
              )}

              {error && (
                <Alert
                  color="red"
                  icon={<IconAlertCircle size={18} />}
                  withCloseButton={!staleForm}
                  onClose={() => setError(null)}
                >
                  {error}
                  {staleForm && !activePublicRunToken && (
                    <Button
                      disabled={submitting}
                      onClick={() => {
                        if (!portalIdentifier) return;
                        const generation = formGeneration.current;
                        setSubmitting(true);
                        void retrievePublicPortal(portalIdentifier)
                          .then((next) => {
                            if (generation !== formGeneration.current) return;
                            // Input definitions may have changed. Retain only unchanged fields for explicit review.
                            setValues((previous) =>
                              Object.fromEntries(
                                next.manual_trigger_inputs.map((input) => {
                                  const old = portal?.manual_trigger_inputs.find(
                                    (item) => item.output_id === input.output_id,
                                  );
                                  return [
                                    input.output_id,
                                    old && JSON.stringify(old) === JSON.stringify(input)
                                      ? previous[input.output_id]
                                      : makeInitialValues([input])[input.output_id],
                                  ];
                                }),
                              ),
                            );
                            setPortal(next);
                            setStaleForm(false);
                            setError('Form refreshed. Review the inputs and submit again.');
                          })
                          .catch((cause) => {
                            if (generation === formGeneration.current)
                              setError(getErrorMessage(cause));
                          })
                          .finally(() => {
                            if (generation === formGeneration.current) setSubmitting(false);
                          });
                      }}
                    >
                      Refresh and review form
                    </Button>
                  )}
                </Alert>
              )}

              {!activePublicRunToken && (
                <>
                  <Stack className={classes.inputBlock} gap={6}>
                    <Text className={classes.inputLabel}>Submission name</Text>
                    <TextInput
                      aria-label="Submission name"
                      placeholder="e.g. March reconciliation"
                      value={runName}
                      onChange={(event) => setRunName(event.currentTarget.value)}
                      disabled={submitting}
                    />
                  </Stack>

                  <Stack gap={22}>
                    {portal.manual_trigger_inputs.map((input) => (
                      <TriggerInputControl
                        key={input.output_id}
                        input={input}
                        value={values[input.output_id]}
                        disabled={submitting}
                        onChange={(value) => updateValue(input.output_id, value)}
                      />
                    ))}
                  </Stack>

                  <Group>
                    <Button
                      fullWidth
                      className={classes.submitButton}
                      color="dark"
                      onClick={handleSubmit}
                      loading={submitting}
                      disabled={submitDisabled}
                    >
                      Run
                    </Button>
                  </Group>
                </>
              )}

              {activePublicRunToken && (
                <Stack gap="lg">
                  <RunStatusPanel
                    portalRun={portalRun}
                    uploading={submitting}
                    runErrorMessage={portal.run_error_message}
                  />
                  {portalRun && canSubmitAnotherRequest(portalRun) && (
                    <Group justify="flex-end">
                      <Button
                        component="a"
                        href={`/portals/${canonicalPortalIdentifier}`}
                        variant="default"
                      >
                        Submit another request
                      </Button>
                    </Group>
                  )}
                  {portalRun && canShowPortalRunOutputs(portalRun) && (
                    <>
                      <Divider />
                      <Stack gap="sm">
                        <Title order={3} size="h5">
                          Outputs
                        </Title>
                        {portalRun.outputs.length > 0 ? (
                          portalRun.outputs.map((output) => (
                            <Group
                              className={classes.outputRow}
                              key={`${output.step_id}:${output.output_id}`}
                              justify="space-between"
                            >
                              <div>
                                <Text fw={500}>{output.name}</Text>
                                {!output.blob_exists && (
                                  <Text size="sm" c="dimmed">
                                    Preparing download...
                                  </Text>
                                )}
                              </div>
                              <Button
                                component="a"
                                href={output.url}
                                target="_blank"
                                rel="noreferrer"
                                color="dark"
                                leftSection={<IconDownload size={16} />}
                                disabled={!output.blob_exists}
                              >
                                Download
                              </Button>
                            </Group>
                          ))
                        ) : (
                          <Text size="sm" c="dimmed">
                            No pinned output files are available for this run yet.
                          </Text>
                        )}
                      </Stack>
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          </section>
        </Stack>
      </main>
    </div>
  );
}

function TriggerInputControl({
  input,
  value,
  disabled,
  onChange,
}: {
  input: PublicPortalManualTriggerInput;
  value: TriggerValue | undefined;
  disabled: boolean;
  onChange: (value: TriggerValue) => void;
}) {
  const label = input.name || input.output_id;
  const description = input.description.trim();
  return (
    <Stack className={classes.inputBlock} gap={6}>
      <div className={classes.inputHeader}>
        <div className={classes.inputHeaderText}>
          <Text className={classes.inputLabel}>{label}</Text>
          {description && (
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          )}
        </div>
        {input.is_required && (
          <Badge className={classes.requiredBadge} variant="dot">
            Required
          </Badge>
        )}
      </div>

      {input.input_type === ManualTriggerInputType.Text && (
        <Textarea
          aria-label={label}
          rows={4}
          disabled={disabled}
          value={value?.type === ManualTriggerInputType.Text ? value.text : ''}
          onChange={(event) =>
            onChange({
              trigger_id: input.output_id,
              type: ManualTriggerInputType.Text,
              text: event.currentTarget.value,
            })
          }
        />
      )}

      {input.input_type === ManualTriggerInputType.Json && (
        <JsonPortalFormControl
          input={input}
          value={value?.type === ManualTriggerInputType.Json ? value.data : {}}
          disabled={disabled}
          onChange={(data) =>
            onChange({
              trigger_id: input.output_id,
              type: ManualTriggerInputType.Json,
              data,
            })
          }
        />
      )}

      {input.input_type === ManualTriggerInputType.File && (
        <Stack gap="xs">
          <CustomDropZone
            mode="single"
            files={value?.type === ManualTriggerInputType.File ? value.files : []}
            setFiles={(files) =>
              onChange({ trigger_id: input.output_id, type: ManualTriggerInputType.File, files })
            }
            height={152}
            variant="portal"
          />
        </Stack>
      )}

      {input.input_type === ManualTriggerInputType.Folder && (
        <Stack gap="xs">
          <CustomDropZone
            mode="multiple"
            files={value?.type === ManualTriggerInputType.Folder ? value.files : []}
            setFiles={(files, nameOrDir) =>
              onChange({
                trigger_id: input.output_id,
                type: ManualTriggerInputType.Folder,
                files,
                name:
                  nameOrDir || (value?.type === ManualTriggerInputType.Folder ? value.name : label),
              })
            }
            height={176}
            variant="portal"
          />
          {value?.type === ManualTriggerInputType.Folder && value.files.length > 0 && (
            <Stack className={classes.uploadFileList} gap="xs">
              {value.files.map((file, index) => (
                <PortalUploadFileRow
                  key={`${file.path}-${index}`}
                  name={file.path}
                  size={file.file.size}
                  onRemove={() => {
                    const nextFiles = value.files.slice();
                    nextFiles.splice(index, 1);
                    onChange({
                      trigger_id: input.output_id,
                      type: ManualTriggerInputType.Folder,
                      files: nextFiles,
                      name: value.name,
                    });
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function JsonPortalFormControl({
  input,
  value,
  disabled,
  onChange,
}: {
  input: PublicPortalManualTriggerInput;
  value: Record<string, unknown>;
  disabled: boolean;
  onChange: (data: Record<string, unknown>) => void;
}) {
  const form = input.portal_form;
  if (!form) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={18} />}>
        This portal input is not available. Please contact the team that shared this portal.
      </Alert>
    );
  }

  const groups = form.fields.reduce<Map<string, PublicPortalJsonFormField[]>>((current, field) => {
    const group = field.group || '';
    const fields = current.get(group) || [];
    fields.push(field);
    current.set(group, fields);
    return current;
  }, new Map());

  return (
    <Stack gap="md">
      {(form.title || form.description) && (
        <Stack gap={2}>
          {form.title && <Text fw={600}>{form.title}</Text>}
          {form.description && (
            <Text size="sm" c="dimmed">
              {form.description}
            </Text>
          )}
        </Stack>
      )}
      {[...groups.entries()].map(([group, fields]) => (
        <Stack key={group || 'fields'} gap="sm">
          {group && <Text fw={500}>{group}</Text>}
          {fields.map((field) => (
            <JsonPortalFormFieldControl
              key={field.path.join('.')}
              field={field}
              value={getNestedValue(value, field.path)}
              disabled={disabled}
              onChange={(nextValue) => onChange(setNestedValue(value, field.path, nextValue))}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

function JsonPortalFormFieldControl({
  field,
  value,
  disabled,
  onChange,
}: {
  field: PublicPortalJsonFormField;
  value: unknown;
  disabled: boolean;
  onChange: (value: unknown) => void;
}) {
  const description = field.help_text || undefined;
  const commonProps = {
    label: field.label,
    description,
    required: field.is_required,
    disabled,
  };

  if (field.control === 'boolean') {
    return (
      <Select
        {...commonProps}
        placeholder={field.placeholder || 'Select yes or no'}
        data={[
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ]}
        value={value === true ? 'true' : value === false ? 'false' : null}
        onChange={(nextValue) => onChange(nextValue === null ? undefined : nextValue === 'true')}
        clearable={!field.is_required}
      />
    );
  }

  if (field.control === 'select') {
    return (
      <Select
        {...commonProps}
        placeholder={field.placeholder || 'Select an option'}
        data={(field.enum_values || []).map((option) => ({ value: option, label: option }))}
        value={typeof value === 'string' ? value : null}
        onChange={(nextValue) => onChange(nextValue ?? undefined)}
        clearable={!field.is_required}
      />
    );
  }

  if (field.control === 'number' || field.control === 'integer') {
    return (
      <NumberInput
        {...commonProps}
        placeholder={field.placeholder || undefined}
        min={field.minimum ?? undefined}
        max={field.maximum ?? undefined}
        allowDecimal={field.control === 'number'}
        decimalScale={field.control === 'integer' ? 0 : undefined}
        value={typeof value === 'number' ? value : ''}
        onChange={(nextValue) =>
          onChange(
            typeof nextValue === 'number' && Number.isFinite(nextValue) ? nextValue : undefined,
          )
        }
      />
    );
  }

  if (field.control === 'textarea') {
    return (
      <Textarea
        {...commonProps}
        placeholder={field.placeholder || undefined}
        minLength={field.min_length ?? undefined}
        maxLength={field.max_length ?? undefined}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.currentTarget.value || undefined)}
        rows={4}
      />
    );
  }

  const inputType =
    field.control === 'date' || field.control === 'email' || field.control === 'url'
      ? field.control
      : 'text';
  return (
    <TextInput
      {...commonProps}
      type={inputType}
      placeholder={field.placeholder || undefined}
      minLength={field.min_length ?? undefined}
      maxLength={field.max_length ?? undefined}
      value={typeof value === 'string' ? value : ''}
      onChange={(event) => onChange(event.currentTarget.value || undefined)}
    />
  );
}

function getNestedValue(data: Record<string, unknown>, path: string[]): unknown {
  let current: unknown = data;
  for (const segment of path) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) return undefined;
    if (!Object.hasOwn(current, segment)) return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function setNestedValue(
  data: Record<string, unknown>,
  path: string[],
  value: unknown,
): Record<string, unknown> {
  const nextData = structuredClone(data);
  let current = nextData;
  for (const segment of path.slice(0, -1)) {
    const existing = Object.hasOwn(current, segment) ? current[segment] : undefined;
    if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
      current[segment] = {};
    }
    current = current[segment] as Record<string, unknown>;
  }
  const finalSegment = path[path.length - 1];
  if (value === undefined || value === '') {
    delete current[finalSegment];
    return removeEmptyObjects(nextData);
  }
  current[finalSegment] = value;
  return nextData;
}

function removeEmptyObjects(data: Record<string, unknown>): Record<string, unknown> {
  for (const [key, value] of Object.entries(data)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const cleaned = removeEmptyObjects(value as Record<string, unknown>);
      if (Object.keys(cleaned).length === 0) {
        delete data[key];
      } else {
        data[key] = cleaned;
      }
    }
  }
  return data;
}

function PortalUploadFileRow({
  name,
  size,
  onRemove,
}: {
  name: string;
  size: number;
  onRemove: () => void;
}) {
  return (
    <Group className={classes.uploadFileRow} justify="space-between" wrap="nowrap" gap="sm">
      <Group gap="sm" wrap="nowrap" className={classes.uploadFileDetails}>
        <div className={classes.uploadFileIcon}>
          <IconFile size={18} />
        </div>
        <Stack gap={0} className={classes.uploadFileText}>
          <Tooltip label={name} openDelay={250}>
            <Text size="sm" fw={500} truncate>
              {name}
            </Text>
          </Tooltip>
          <Text size="xs" c="dimmed">
            {formatBytes(size, 1)}
          </Text>
        </Stack>
      </Group>
      <Button
        variant="subtle"
        color="gray"
        size="compact-sm"
        aria-label={`Delete ${name}`}
        onClick={onRemove}
      >
        <IconX size={16} />
      </Button>
    </Group>
  );
}

function RunStatusBadge({ status }: { status: ExecutionStatus }) {
  if (status === ExecutionStatus.Completed) {
    return <Badge color="green">Complete</Badge>;
  }
  if (status === ExecutionStatus.PartiallyCompleted) {
    return <Badge color="yellow">Completed with issues</Badge>;
  }
  const issueLabel = PORTAL_RUN_ISSUE_LABELS[status];
  if (issueLabel) {
    return <Badge color="red">{issueLabel.badge}</Badge>;
  }
  if (status === ExecutionStatus.ActiveWithErrors) {
    return <Badge color="yellow">Running with issues</Badge>;
  }
  return <Badge color="blue">Running</Badge>;
}

function shouldPollPortalRun(portalRun: PublicPortalRun) {
  if (!SUBMIT_ANOTHER_STATUSES.has(portalRun.status)) return true;
  return (
    canShowPortalRunOutputs(portalRun) && portalRun.outputs.some((output) => !output.blob_exists)
  );
}

function canSubmitAnotherRequest(portalRun: PublicPortalRun) {
  return SUBMIT_ANOTHER_STATUSES.has(portalRun.status);
}

function canShowPortalRunOutputs(portalRun: PublicPortalRun) {
  return OUTPUT_READY_STATUSES.has(portalRun.status);
}

function RunStatusPanel({
  portalRun,
  uploading,
  runErrorMessage,
}: {
  portalRun: PublicPortalRun | null;
  uploading: boolean;
  runErrorMessage: string;
}) {
  const status = portalRun?.status;
  const isRunning = !status || !SUBMIT_ANOTHER_STATUSES.has(status);
  const hasFailure = status ? FAILURE_STATUSES.has(status) : false;
  const needsAttention = status === ExecutionStatus.AskUser;
  const isPartialSuccess = status === ExecutionStatus.PartiallyCompleted;
  const isSuccess = status === ExecutionStatus.Completed;
  const isRunningWithIssues = status === ExecutionStatus.ActiveWithErrors;
  const issueLabel = status ? PORTAL_RUN_ISSUE_LABELS[status] : undefined;

  return (
    <Group className={classes.statusBar} gap="sm" align="start">
      {uploading || isRunning ? (
        <Loader size="sm" />
      ) : hasFailure || needsAttention ? (
        <IconAlertCircle size={20} color="#d9480f" />
      ) : isPartialSuccess ? (
        <IconAlertTriangle size={20} color="#f08c00" />
      ) : (
        <IconCircleCheck size={20} color="#2f9e44" />
      )}
      <Stack gap={2}>
        <Text fw={600}>
          {uploading
            ? 'Uploading inputs'
            : !portalRun
              ? 'Starting run'
              : issueLabel
                ? issueLabel.heading
                : isPartialSuccess
                  ? 'Run completed with issues'
                  : isSuccess
                    ? 'Run complete'
                    : 'Run in progress'}
        </Text>
        {portalRun?.preprocessing_error ? (
          <Text size="sm" c="red">
            {portalRun.preprocessing_error}
          </Text>
        ) : hasFailure || needsAttention ? (
          <Text size="sm" c="red">
            {runErrorMessage}
          </Text>
        ) : isPartialSuccess ? (
          <Text size="sm" c="dimmed">
            Some steps did not complete successfully. Available pinned output files are shown below.
          </Text>
        ) : isRunningWithIssues ? (
          <Text size="sm" c="dimmed">
            Some work has failed, but the run is still in progress. This page will update
            automatically.
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            {isSuccess
              ? 'Pinned output files are available below when ready.'
              : 'This page will update automatically.'}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

function makeInitialValues(inputs: PublicPortalManualTriggerInput[]): Record<string, TriggerValue> {
  return Object.fromEntries(
    inputs.map((input) => {
      if (input.input_type === ManualTriggerInputType.Text) {
        return [input.output_id, { trigger_id: input.output_id, type: input.input_type, text: '' }];
      }
      if (input.input_type === ManualTriggerInputType.Json) {
        return [input.output_id, { trigger_id: input.output_id, type: input.input_type, data: {} }];
      }
      if (input.input_type === ManualTriggerInputType.Folder) {
        return [
          input.output_id,
          { trigger_id: input.output_id, type: input.input_type, files: [], name: input.name },
        ];
      }
      return [input.output_id, { trigger_id: input.output_id, type: input.input_type, files: [] }];
    }),
  );
}

function hasValue(value: TriggerValue | undefined): boolean {
  if (!value) return false;
  if (value.type === ManualTriggerInputType.Text) {
    return value.text.trim().length > 0;
  }
  if (value.type === ManualTriggerInputType.Json) {
    return hasJsonValue(value.data);
  }
  return value.files.length > 0;
}

function hasJsonValue(data: Record<string, unknown>): boolean {
  return Object.values(data).some((value) => {
    if (value === undefined || value === null || value === '') return false;
    if (typeof value === 'object' && !Array.isArray(value)) {
      return hasJsonValue(value as Record<string, unknown>);
    }
    return true;
  });
}

function validatePortalInputs(
  inputs: PublicPortalManualTriggerInput[],
  values: Record<string, TriggerValue>,
): string | null {
  let totalFiles = 0;
  for (const value of Object.values(values)) {
    if (value.type === ManualTriggerInputType.Text) {
      if (value.text.length > MAX_PORTAL_TEXT_INPUT_CHARS) {
        return `Text inputs must be ${MAX_PORTAL_TEXT_INPUT_CHARS.toLocaleString()} characters or fewer.`;
      }
      if (value.text.trim().length > 0) {
        totalFiles += 1;
      }
      continue;
    }

    if (value.type === ManualTriggerInputType.Json) {
      const input = inputs.find((candidate) => candidate.output_id === value.trigger_id);
      if (!input?.portal_form) return 'This portal input is not available.';
      if (!input.is_required && !hasJsonValue(value.data)) continue;
      const formError = validateJsonPortalForm(input.portal_form.fields, value.data);
      if (formError) return formError;
      continue;
    }

    if (value.type === ManualTriggerInputType.Folder) {
      if (value.files.length > MAX_PORTAL_DIRECTORY_FILES) {
        return `Folder inputs can include at most ${MAX_PORTAL_DIRECTORY_FILES} files.`;
      }
      totalFiles += value.files.length;
    } else {
      totalFiles += value.files.length;
    }

    const longPath = value.files.find((file) => file.path.length > MAX_PORTAL_UPLOAD_PATH_LENGTH);
    if (longPath) {
      return `Upload paths must be ${MAX_PORTAL_UPLOAD_PATH_LENGTH} characters or fewer.`;
    }
  }

  if (totalFiles > MAX_PORTAL_TOTAL_UPLOAD_FILES) {
    return `Portal runs can include at most ${MAX_PORTAL_TOTAL_UPLOAD_FILES} files.`;
  }

  return null;
}

function validateJsonPortalForm(
  fields: PublicPortalJsonFormField[],
  data: Record<string, unknown>,
): string | null {
  for (const field of fields) {
    const value = getNestedValue(data, field.path);
    if (field.is_required && isEmptyPortalFormValue(value)) {
      return `${field.label} is required.`;
    }
    if (isEmptyPortalFormValue(value)) continue;
    if ((field.control === 'number' || field.control === 'integer') && typeof value !== 'number') {
      return `${field.label} must be a number.`;
    }
    if (field.control === 'integer' && typeof value === 'number' && !Number.isInteger(value)) {
      return `${field.label} must be a whole number.`;
    }
    if (
      typeof value === 'number' &&
      field.minimum !== null &&
      field.minimum !== undefined &&
      value < field.minimum
    ) {
      return `${field.label} must be at least ${field.minimum}.`;
    }
    if (
      typeof value === 'number' &&
      field.maximum !== null &&
      field.maximum !== undefined &&
      value > field.maximum
    ) {
      return `${field.label} must be at most ${field.maximum}.`;
    }
    if (typeof value === 'string') {
      if (
        field.min_length !== null &&
        field.min_length !== undefined &&
        value.length < field.min_length
      ) {
        return `${field.label} must be at least ${field.min_length} characters.`;
      }
      if (
        field.max_length !== null &&
        field.max_length !== undefined &&
        value.length > field.max_length
      ) {
        return `${field.label} must be ${field.max_length} characters or fewer.`;
      }
      if (field.control === 'select' && field.enum_values && !field.enum_values.includes(value)) {
        return `${field.label} must be one of the available options.`;
      }
      if (field.control === 'date' && !isValidPortalDate(value)) {
        return `${field.label} must be a valid date.`;
      }
      if (field.control === 'email' && !isValidPortalInputFormat('email', value)) {
        return `${field.label} must be a valid email address.`;
      }
      if (field.control === 'url' && !isValidPortalInputFormat('url', value)) {
        return `${field.label} must be a valid URL.`;
      }
    }
  }
  return null;
}

function isValidPortalDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

function isValidPortalInputFormat(type: 'email' | 'url', value: string): boolean {
  const input = document.createElement('input');
  input.type = type;
  input.value = value;
  return input.checkValidity();
}

function isEmptyPortalFormValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

function makeTriggerRequests(
  inputs: PublicPortalManualTriggerInput[],
  values: Record<string, TriggerValue>,
): (
  | PublicPortalTriggerFileRequest
  | PublicPortalTriggerDirectoryRequest
  | PublicPortalTriggerJsonRequest
)[] {
  return inputs
    .map((input) => {
      const value = values[input.output_id];
      if (!hasValue(value)) return null;

      if (value.type === ManualTriggerInputType.Text) {
        const name = input.name || 'Text input';
        return {
          trigger_id: value.trigger_id,
          name,
          path: `${name}.txt`,
          content_type: 'text/plain',
        };
      }
      if (value.type === ManualTriggerInputType.File) {
        const file = value.files[0].file;
        return {
          trigger_id: value.trigger_id,
          name: file.name,
          path: file.name,
          content_type: file.type ? normalizeUploadContentType(file.type) : null,
        };
      }
      if (value.type === ManualTriggerInputType.Json) {
        return {
          trigger_id: value.trigger_id,
          data: value.data,
        };
      }

      return {
        trigger_id: value.trigger_id,
        name: value.name || input.name || 'Folder input',
        files: value.files.map(relativeFileToUploadMetadata),
      };
    })
    .filter(
      (
        request,
      ): request is
        | PublicPortalTriggerFileRequest
        | PublicPortalTriggerDirectoryRequest
        | PublicPortalTriggerJsonRequest => Boolean(request),
    );
}

async function uploadTriggerInputs(
  response: PublicPortalRunCreateResponse,
  values: Record<string, TriggerValue>,
) {
  const uploads = response.triggers.flatMap((triggerResponse) =>
    makeUploadPromises(triggerResponse, values[triggerResponse.trigger_id]),
  );
  await Promise.all(uploads);
}

function makeUploadPromises(
  triggerResponse: PublicPortalRunTriggerResponse,
  value: TriggerValue | undefined,
) {
  if (!value) return [];

  if (value.type === ManualTriggerInputType.Text) {
    const url = getSingleUploadUrl(triggerResponse.urls);
    return [
      axios.put(url, new Blob([value.text], { type: 'text/plain' }), {
        headers: { 'Content-Type': 'text/plain' },
      }),
    ];
  }

  if (value.type === ManualTriggerInputType.Json) {
    return [];
  }

  if (value.type === ManualTriggerInputType.File) {
    const file = value.files[0]?.file;
    const url = getSingleUploadUrl(triggerResponse.urls);
    const contentType = file?.type ? normalizeUploadContentType(file.type) : null;
    return file ? [axios.put(url, file, getSignedUploadConfig(contentType))] : [];
  }

  return value.files.reduce<Promise<unknown>[]>((promises, relativeFile) => {
    const metadata = relativeFileToUploadMetadata(relativeFile);
    if (triggerResponse.unsupported_files.includes(metadata.path)) return promises;

    const url = triggerResponse.urls[metadata.path];
    if (!url) throw new Error(`Missing upload URL for ${metadata.path}`);

    promises.push(axios.put(url, relativeFile.file, getSignedUploadConfig(metadata.content_type)));
    return promises;
  }, []);
}

function getSingleUploadUrl(urls: Record<string, string>) {
  const url = Object.values(urls)[0];
  if (!url) throw new Error('Missing upload URL');
  return url;
}

function getUploadFailureMessage() {
  return 'The run was created, but one or more input files did not finish uploading. Please submit another request and re-upload the inputs.';
}

export default PublicPortalPage;
